import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEO from '../components/SEO';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'Are the casinos on SpinHarvester safe?',
      answer: 'Yes, absolutely. We only review casinos that are licensed by the UK Gambling Commission. Every casino featured on our site has been thoroughly vetted for security, fairness, and regulatory compliance. We verify licenses, test customer service, and check payout reliability before recommending any casino.'
    },
    {
      question: 'How do I claim the bonuses shown on your site?',
      answer: 'To claim a bonus, simply click through our exclusive links to the casino website. Register a new account and make your first deposit. The bonus will be automatically credited to your account, or you may need to enter a bonus code during registration. Always check the specific terms and conditions for each offer.'
    },
    {
      question: 'What are wagering requirements?',
      answer: 'Wagering requirements (also called playthrough requirements) specify how many times you must bet the bonus amount before you can withdraw winnings. For example, if you receive a £100 bonus with 35x wagering, you need to wager £3,500 before withdrawing. We always clearly display these requirements in our reviews.'
    },
    {
      question: 'How long do withdrawals take?',
      answer: 'Withdrawal times vary by casino and payment method. Most UK-licensed casinos process withdrawals within 24-48 hours for e-wallets, and 3-5 business days for bank transfers. We test and verify actual payout times for each casino we review, so you know what to expect.'
    },
    {
      question: 'Do I need to pay to use SpinHarvester?',
      answer: 'No, SpinHarvester is completely free to use. We provide all our reviews, comparisons, and information at no cost to you. We may receive commissions from casinos when you sign up through our links, but this never affects our reviews or recommendations.'
    },
    {
      question: 'Can I trust your casino ratings?',
      answer: 'Yes, our ratings are based on comprehensive testing by our expert team. We evaluate multiple factors including game selection, bonus terms, customer service, payout speed, security, and overall user experience. Our reviews are honest and unbiased, and we update them regularly to reflect current conditions.'
    },
    {
      question: 'What makes a casino "UK-licensed"?',
      answer: 'A UK-licensed casino holds a valid license from the UK Gambling Commission (UKGC). This license ensures the casino operates legally in the UK, follows strict regulations for player protection, uses secure payment processing, and provides fair games. We verify every casino\'s license status before featuring them.'
    },
    {
      question: 'How often do you update your reviews?',
      answer: 'We regularly update our reviews to reflect changes in bonuses, terms, game selections, and overall casino performance. Major updates happen quarterly, but we also make real-time updates when significant changes occur, such as new bonus offers or policy changes.'
    },
    {
      question: 'What should I look for when choosing a casino?',
      answer: 'Key factors to consider include: UK Gambling Commission license, competitive bonuses with fair terms, fast payout times, a good selection of games from reputable providers, reliable customer support, secure payment methods, and positive player reviews. Our comparison tool helps you evaluate all these factors easily.'
    },
    {
      question: 'Is online gambling legal in the UK?',
      answer: 'Yes, online gambling is legal in the UK for players aged 18 and over. However, you must play at casinos licensed by the UK Gambling Commission. Playing at unlicensed casinos is illegal and unsafe. We only feature UKGC-licensed operators to ensure you stay within the law.'
    }
  ];

  return (
    <>
      <SEO
        title="FAQ - Frequently Asked Questions About UK Casinos"
        description="Find answers to common questions about UK online casinos, bonuses, wagering requirements, and more. Get expert guidance on safe and responsible gambling."
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
            }
          }))
        }}
      />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600">
              Everything you need to know about UK online casinos
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openIndex === index ? (
                    <FaChevronUp className="text-primary-600 flex-shrink-0" />
                  ) : (
                    <FaChevronDown className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-5 text-gray-700 leading-relaxed slide-in-from-top">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;

